import { IDatabaseColumnModel } from "@kuika/kuika-cl-model";
import { cellValidationMessage, isRequiredCellMissing } from "./cell-validation";

export type TableEditorCellVisualTone = "clean" | "dirty" | "new" | "error";

export interface ITableEditorCellVisualState {
  tone: TableEditorCellVisualTone;
  title?: string;
}

export interface IPersistedCellVisualStateOptions {
  column: IDatabaseColumnModel;
  value: unknown;
  dirty: boolean;
  saveIssue?: string;
}

export interface IInsertCellVisualStateOptions {
  column: IDatabaseColumnModel;
  value: unknown;
  insertHasAnyValue: boolean;
  hasEditedValue: boolean;
  saveIssue?: string;
}

export const persistedCellVisualState = (options: IPersistedCellVisualStateOptions): ITableEditorCellVisualState => {
  if (!options.dirty) {
    return { tone: "clean" };
  }
  const validationMessage: string | null = cellValidationMessage(options.column, options.value);
  if (validationMessage !== null) {
    return { tone: "error", title: validationMessage };
  }
  if (options.saveIssue !== undefined) {
    return { tone: "error", title: options.saveIssue };
  }
  return { tone: "dirty" };
};

export const insertCellVisualState = (options: IInsertCellVisualStateOptions): ITableEditorCellVisualState => {
  // Required-missing fires only once the user has started filling the
  // row (or after the server already flagged the cell). An untouched
  // draft row staying quiet is intentional — we don't yell at a cell
  // for being empty before the user has expressed any intent.
  const shouldValidateRequired: boolean = options.insertHasAnyValue || options.saveIssue !== undefined;
  if (shouldValidateRequired && isRequiredCellMissing(options.column, options.value)) {
    return { tone: "error", title: cellValidationMessage(options.column, options.value) ?? options.saveIssue };
  }
  // Type-format validation (GUID shape, Int range, Decimal scale,
  // String max length, Date / Datetime / DatetimeOffset format) only
  // applies once the user has typed something into the cell. Pre-fix
  // the visual layer only checked GUID here, so an out-of-range Int or
  // an over-long String stayed quiet until Save and the user got a
  // surprise validation alert instead of inline feedback while typing.
  if (options.hasEditedValue) {
    const validationMessage: string | null = cellValidationMessage(options.column, options.value);
    if (validationMessage !== null) {
      return { tone: "error", title: validationMessage };
    }
  }
  if (options.saveIssue !== undefined && options.hasEditedValue) {
    return { tone: "error", title: options.saveIssue };
  }
  if (options.hasEditedValue) {
    return { tone: "new" };
  }
  return { tone: "clean" };
};
